import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageCircle, Instagram, Send, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";
import { track, identifyUser, EVENTS } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      // Tag as contact-source subscriber (message logged client-side toast for now)
      await axios.post(`${API}/subscribe`, {
        first_name: firstName.trim(),
        email: email.trim(),
        source: "contact_form",
      });
      identifyUser(email, { first_name: firstName.trim(), source: "contact_form" });
      track(EVENTS.CONTACT_SUBMITTED, { source: "contact_form", message_length: message.trim().length });
      setSent(true);
      toast.success("Thanks — we'll get back to you soon.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your info and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid="contact-page">
      <BrandHeader variant="cream" />

      <section className="bg-[#F8F6F2] pt-16 pb-14 md:pt-24 md:pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Contact</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-medium tracking-tight text-[#333333] leading-[1.05]">
            Say hello. <span className="italic">We read everything.</span>
          </h1>
          <p className="mt-5 text-lg md:text-xl text-[#4A4A4A] max-w-2xl leading-relaxed">
            Questions, feedback, partnerships, or a story you want to share — we&apos;d love to hear from you.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#8FAF8A]/12 text-[#6E8F6A] flex-none">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <div className="font-display font-bold text-[#333333]">Email</div>
                <a href="mailto:support@wishingwellnesslife.com" className="text-[#4A4A4A] hover:text-[#333333]" data-testid="contact-email-link">
                  support@wishingwellnesslife.com
                </a>
                <div className="text-sm text-[#8A8A8A] mt-1">Replies within 2 business days.</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F6C453]/25 text-[#B8860B] flex-none">
                <Instagram className="h-5 w-5" />
              </span>
              <div>
                <div className="font-display font-bold text-[#333333]">Social</div>
                <ul className="mt-1 space-y-1 text-[#4A4A4A]">
                  <li>
                    <a href="https://instagram.com/TheStrongForce.Daily" target="_blank" rel="noopener noreferrer" className="hover:text-[#333333]" data-testid="contact-instagram">
                      Instagram · @TheStrongForce.Daily
                    </a>
                  </li>
                  <li>
                    <a href="https://www.tiktok.com/@thestrongforcedaily" target="_blank" rel="noopener noreferrer" className="hover:text-[#333333]" data-testid="contact-tiktok-tsfd">
                      TikTok · @thestrongforcedaily
                    </a>
                  </li>
                  <li>
                    <a href="https://www.tiktok.com/@wish.ingwellness" target="_blank" rel="noopener noreferrer" className="hover:text-[#333333]" data-testid="contact-tiktok-ww">
                      TikTok · @wish.ingwellness
                    </a>
                  </li>
                  <li>
                    <a href="https://pin.it/1Xjp7YwfZ" target="_blank" rel="noopener noreferrer" className="hover:text-[#333333]" data-testid="contact-pinterest">
                      Pinterest · Wishing Wellness
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#8FAF8A]/12 text-[#6E8F6A] flex-none">
                <MessageCircle className="h-5 w-5" />
              </span>
              <div>
                <div className="font-display font-bold text-[#333333]">Community</div>
                <div className="text-[#4A4A4A]">Inner Circle launching soon.</div>
                <Link to="/inner-circle" className="text-sm text-[#6E8F6A] font-semibold hover:underline mt-1 inline-block">Join the waitlist →</Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {sent ? (
              <div className="bg-[#F8F6F2] rounded-3xl border border-[#EDE9E1] p-10 text-center" data-testid="contact-success">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#8FAF8A]">
                  <Check className="h-5 w-5 text-white" />
                </span>
                <h3 className="mt-5 font-display font-bold text-2xl text-[#333333]">Thanks, {firstName}.</h3>
                <p className="mt-3 text-[#4A4A4A]">Your note is with us. We&apos;ll write back soon — usually within two business days.</p>
                <Button asChild className="mt-6 rounded-full bg-[#333333] hover:bg-black text-white h-12 px-6 font-semibold" data-testid="contact-back-home">
                  <Link to="/">Back to Home <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#8A8A8A] mb-2">First Name</label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Your first name" className="h-13 rounded-2xl bg-white border-[#EDE9E1] px-5 text-base focus-visible:ring-2 focus-visible:ring-[#8FAF8A] focus-visible:ring-offset-0" data-testid="contact-input-first-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#8A8A8A] mb-2">Email</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-13 rounded-2xl bg-white border-[#EDE9E1] px-5 text-base focus-visible:ring-2 focus-visible:ring-[#8FAF8A] focus-visible:ring-offset-0" data-testid="contact-input-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#8A8A8A] mb-2">Message</label>
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us what's on your mind…" rows={6} className="rounded-2xl bg-white border-[#EDE9E1] px-5 py-4 text-base focus-visible:ring-2 focus-visible:ring-[#8FAF8A] focus-visible:ring-offset-0" data-testid="contact-input-message" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-14 rounded-full bg-[#333333] hover:bg-black text-white text-base font-semibold shadow-lg shadow-black/10 transition-all hover:-translate-y-0.5 disabled:opacity-70" data-testid="contact-submit">
                  {loading ? "Sending…" : "Send Message"}
                  {!loading && <Send className="ml-2 h-4 w-4" />}
                </Button>
                <p className="text-sm text-[#8A8A8A] pt-1" data-testid="contact-microcopy">
                  By sending you&apos;ll also join our weekly newsletter. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <BrandFooter />
    </div>
  );
}
